import { Autocomplete, TextField } from "@mui/material";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";
import { useEffect, useState, useCallback } from "react";
import { Modal, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditButton } from "components/Buttons/EditButton";
import { DeleteButton } from "components/Buttons/DeleteButton";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import SubmitButton from "components/Buttons/SubmitButton";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SaveCancelButtons from "components/Buttons/SaveCancelButtons";
import { showConfirm } from "utils/swalHelper";

interface BagOption {
  inventory_item_id: string | number;
  bag_no: string;
  available_quantity: number;
}

interface BagRow {
  id: string;
  inventory_item_id: string | number;
  bag_no: string;
  available_quantity: number;
  bag_item_dispatch_quantity: number;
}

interface BagWithoutId {
  inventory_item_id: string | number;
  bag_no: string;
  total_quantity: number;
  bag_item_dispatch_quantity: number;
}

interface DispatchBagSelectionModalProps {
  show: boolean;
  handleClose: () => void;
  quantity: number; // Total quantity available for the item
  onSave: (bags: BagWithoutId[]) => void;
  initialBags?: BagWithoutId[];
  itemName?: string;
  categoryName?: string;
  subCategoryName?: string;
  warehouseId?: string | number;
  isStockTransfer?: boolean; // Indicates if this is for stock transfer dispatch
  readOnly?: boolean; // If true, modal is in view-only mode
  isEditMode?: boolean; // Indicates if this is in edit mode
}

const DispatchBagSelectionModal: React.FC<DispatchBagSelectionModalProps> = ({
  show,
  handleClose,
  quantity,
  onSave,
  initialBags = [],
  itemName,
  categoryName,
  subCategoryName,
  warehouseId,
  isStockTransfer = false,
  readOnly = false,
  isEditMode = false,
}) => {
  const [bagOptions, setBagOptions] = useState<BagOption[]>([]);
  const [rows, setRows] = useState<BagRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingBagId, setEditingBagId] = useState<string | null>(null);

  const fetchBagDropdown = useCallback(async () => {
    if (!warehouseId || !itemName || !categoryName || !subCategoryName) {
      setBagOptions([]);
      return;
    }

    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        item_name: itemName || "",
        category_name: categoryName || "",
        sub_category_name: subCategoryName || "",
      });

      if (warehouseId) {
        queryParams.append("warehouse_id", String(warehouseId));
      }

      // Add isStockTransfer parameter if this is for stock transfer
      if (isStockTransfer) {
        queryParams.append("apply_quantity_filter", "true");
      }

      const result = await Axios.get(
        `${api?.warehouse?.item_inward?.bag_dropdown}?${queryParams.toString()}`
      );
      setBagOptions(result?.data?.data || []);
    } catch (err) {
      toast.error("Failed to fetch bag dropdown");
    } finally {
      setLoading(false);
    }
  }, [itemName, categoryName, subCategoryName, warehouseId, isStockTransfer]);

  // Fetch bag dropdown when modal opens and reset state when it closes
  useEffect(() => {
    if (show) {
      // Reset all state when modal opens
      setRows([]);
      setEditingBagId(null);
      formik.resetForm();
      
      // Fetch bag dropdown
      fetchBagDropdown();
      
      // Initialize rows with initialBags after a brief delay to ensure bagOptions are loaded
      if (initialBags && initialBags.length > 0) {
        const bagsWithId: BagRow[] = initialBags.map((bag, idx) => ({
          ...bag,
          id: `bag-${Date.now()}-${idx}`,
          available_quantity: 0, // Will be set from bagOptions in next effect
        }));
        setRows(bagsWithId);
      }
    } else {
      // Clean up when modal closes
      setRows([]);
      setBagOptions([]);
      setEditingBagId(null);
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialBags, fetchBagDropdown]);
  console.log("Row : ",rows)
  // Update available_quantity from bagOptions when they're loaded
  useEffect(() => {
    if (bagOptions.length > 0 && rows.length > 0) {
      setRows((prevRows) => {
        // Only update if there are rows that need available_quantity
        const needsUpdate = prevRows.some(
          (row) =>
            row.available_quantity === 0 &&
            bagOptions.some(
              (opt) =>
                opt.bag_no === row.bag_no &&
                String(opt.inventory_item_id) === String(row.inventory_item_id)
            )
        );
        if (!needsUpdate) return prevRows;

        return prevRows.map((row) => {
          const bagOption = bagOptions.find(
            (opt) =>
              opt.bag_no === row.bag_no &&
              String(opt.inventory_item_id) === String(row.inventory_item_id)
          );
          if (bagOption && row.available_quantity === 0) {
            return {
              ...row,
              available_quantity: bagOption.available_quantity,
            };
          }
          return row;
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bagOptions]);

  const validationSchema = Yup.object({
    bag_no: Yup.object().nullable().required("Bag No. is required"),
    available_quantity: Yup.number()
      .typeError("Available Quantity must be a number")
      .positive("Available Quantity must be greater than 0")
      .required("Available Quantity is required"),
    bag_item_dispatch_quantity: Yup.number()
      .typeError("Bag Item Dispatch Quantity must be a number")
      .positive("Bag Item Dispatch Quantity must be greater than 0")
      .required("Bag Item Dispatch Quantity is required")
      .test(
        "less-than-available",
        "Bag Item Dispatch Quantity must be less than or equal to Available Quantity",
        function (value) {
          const availableQty = parseFloat(this.parent.available_quantity) || 0;
          const dispatchQty = parseFloat(String(value)) || 0;
          return dispatchQty <= availableQty;
        }
      ),
  });

  interface FormValues {
    bag_no: BagOption | null;
    available_quantity: string | number;
    bag_item_dispatch_quantity: string | number;
  }

  const formik = useFormik<FormValues>({
    initialValues: {
      bag_no: null,
      available_quantity: "",
      bag_item_dispatch_quantity: "",
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      const dispatchQty =
        parseFloat(String(values.bag_item_dispatch_quantity)) || 0;
      const availableQty = parseFloat(String(values.available_quantity)) || 0;

      // Check dispatch quantity <= available quantity
      if (dispatchQty > availableQty) {
        formik.setFieldError(
          "bag_item_dispatch_quantity",
          `Bag Item Dispatch Quantity (${dispatchQty}) cannot exceed Available Quantity (${availableQty})`
        );
        toast.error(
          `Bag Item Dispatch Quantity (${dispatchQty}) cannot exceed Available Quantity (${availableQty})`
        );
        return;
      }

      if (!values.bag_no) {
        formik.setFieldError("bag_no", "Bag No. is required");
        return;
      }

      // Extract bag_no after null check so TypeScript knows it's not null
      const selectedBag = values.bag_no;

      // Check total dispatch quantity (excluding the row being edited)
      const rowsExcludingEdited = rows.filter((row) => row.id !== editingBagId);
      const currentTotal = rowsExcludingEdited.reduce(
        (sum, row) =>
          sum + (parseFloat(String(row.bag_item_dispatch_quantity)) || 0),
        0
      );
      const newTotal = currentTotal + dispatchQty;

      if (newTotal > quantity) {
        formik.setFieldError(
          "bag_item_dispatch_quantity",
          `Total Bag Item Dispatch Quantity (${newTotal.toFixed(
            2
          )}) cannot exceed ${quantity}`
        );
        toast.error(
          `Total Bag Item Dispatch Quantity (${newTotal.toFixed(
            2
          )}) cannot exceed ${quantity}`
        );
        return;
      }

      // Check if bag already exists (excluding the row being edited)
      const existingBag = rowsExcludingEdited.find(
        (row) =>
          row.bag_no === selectedBag.bag_no &&
          String(row.inventory_item_id) ===
            String(selectedBag.inventory_item_id)
      );

      if (existingBag) {
        toast.warning(
          `Bag "${selectedBag.bag_no}" already added. Please edit the existing bag to update the quantity.`
        );
        return;
      }

      if (editingBagId !== null) {
        // Update existing row
        const updatedRows = rows.map((row) =>
          row.id === editingBagId
            ? {
                id: editingBagId,
                inventory_item_id: selectedBag.inventory_item_id,
                bag_no: selectedBag.bag_no,
                available_quantity: availableQty,
                bag_item_dispatch_quantity: dispatchQty,
              }
            : row
        );
        setRows(updatedRows);
        setEditingBagId(null);
      } else {
        // Add new row
        const newRow: BagRow = {
          id: Date.now().toString(),
          inventory_item_id: selectedBag.inventory_item_id,
          bag_no: selectedBag.bag_no,
          available_quantity: availableQty,
          bag_item_dispatch_quantity: dispatchQty,
        };
        setRows([...rows, newRow]);
      }

      resetForm();
    },
  });

  const handleDelete = (id: string) => {
    const rowToDelete = rows.find((row) => row.id === id);
    const bagNo = rowToDelete?.bag_no || "this bag";
    
    showConfirm(
      `Are you sure you want to delete bag "${bagNo}"? This action cannot be undone.`,
      "Delete Bag?",
      "Yes, Delete",
      "Cancel"
    ).then((result) => {
      if (result.isConfirmed) {
        setRows(rows.filter((row) => row.id !== id));
        if (editingBagId === id) {
          setEditingBagId(null);
          formik.resetForm();
        }
        toast.success(`Bag "${bagNo}" deleted successfully`);
      }
    });
  };

  const handleEdit = (row: BagRow) => {
    setEditingBagId(row.id);
    // Find the bag option that matches this row
    const bagOption = bagOptions.find(
      (option) =>
        option.bag_no === row.bag_no &&
        String(option.inventory_item_id) === String(row.inventory_item_id)
    ) || {
      inventory_item_id: row.inventory_item_id,
      bag_no: row.bag_no,
      available_quantity: row.available_quantity,
    };

    formik.setFieldValue("bag_no", bagOption);
    formik.setFieldValue("available_quantity", row.available_quantity);
    formik.setFieldValue(
      "bag_item_dispatch_quantity",
      row.bag_item_dispatch_quantity
    );
  };

  const handleCancelEdit = () => {
    setEditingBagId(null);
    formik.resetForm();
  };

  const handleSave = () => {
    const totalDispatchQuantity = rows.reduce(
      (sum, row) =>
        sum + (parseFloat(String(row.bag_item_dispatch_quantity)) || 0),
      0
    );

    if (totalDispatchQuantity > quantity) {
      toast.error(
        `Total Dispatch Quantity (${totalDispatchQuantity}) cannot exceed ${quantity}`
      );
      return;
    }

    if (rows.length === 0) {
      toast.error("Please add at least one bag");
      return;
    }

    // Remove id field from bags before saving (id is only for internal modal use)
    // Map available_quantity to total_quantity for BagWithoutId interface
    const bagsToSave: BagWithoutId[] = rows.map(
      ({ id, available_quantity, ...bag }) => ({
        inventory_item_id: bag.inventory_item_id,
        bag_no: bag.bag_no,
        total_quantity: available_quantity, // Map available_quantity to total_quantity
        bag_item_dispatch_quantity: bag.bag_item_dispatch_quantity,
      })
    );
    onSave(bagsToSave);
    handleClose();
  };

  const handleModalClose = () => {
    // Reset all state when modal is closed
    formik.resetForm();
    setEditingBagId(null);
    setRows([]);
    setBagOptions([]);
    handleClose();
  };

  const totalDispatchQuantity = rows.reduce(
    (sum, row) =>
      sum + (parseFloat(String(row.bag_item_dispatch_quantity)) || 0),
    0
  );
  const remainingQuantity = quantity - totalDispatchQuantity;

  return (
    <Modal show={show} onHide={handleModalClose} centered size={readOnly ? "lg" : "xl"}>
      <Modal.Header className="border-0">
        <Modal.Title className="fw-bold">
          {readOnly ? "View Bag Details" : "Select Bags"}
        </Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={handleModalClose}
        />
      </Modal.Header>
      <hr />
      <Modal.Body>
        {!readOnly && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                Entered Quantity:
              </span>
              <span
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  color: "#212529",
                }}
              >
                {quantity}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                Total Bag Item Dispatch Quantity:
              </span>
              <span
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  color: "#212529",
                }}
              >
                {totalDispatchQuantity.toFixed(2)}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                Remaining Quantity:
              </span>
              <span
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  color: remainingQuantity >= 0 ? "#28a745" : "#dc3545",
                }}
              >
                {remainingQuantity.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        <style>
          {`
            .sticky-column {
              position: relative;
            }
            .sticky-column::before {
              content: '';
              position: absolute;
              left: 0;
              top: 0;
              bottom: 0;
              width: 1px;
              background-color: #dee2e6;
              z-index: 12;
            }
            thead .sticky-column::before {
              z-index: 13;
            }
          `}
        </style>
        <div className="mb-4">
          <Table hover responsive className="align-middle text-center">
            <thead>
              <tr>
                <th style={{ ...tableStyles.headerAlt, minWidth: "80px" }}>
                  Sr No.
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                  Bag No.
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                  Available Quantity
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                  Bag Item Dispatch Quantity
                </th>
                {!readOnly && (
                  <th
                    className="sticky-column"
                    style={{
                      ...tableStyles.headerAlt,
                      ...tableStyles.actionHeader,
                      minWidth: "100px",
                    }}
                  >
                    Action
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {/* Input Row - Only show if not in read-only mode */}
              {!readOnly && (
                <tr>
                  <td style={tableStyles.cell}></td>
                  <td style={tableStyles.cell}>
                    <Autocomplete
                      size="small"
                      options={bagOptions || []}
                      autoHighlight
                      loading={loading}
                      disabled={loading || editingBagId !== null}
                      value={editingBagId !== null ? null : formik.values.bag_no}
                      onChange={(_, val) => {
                        formik.setFieldValue("bag_no", val);
                        if (val) {
                          formik.setFieldValue(
                            "available_quantity",
                            val?.available_quantity || ""
                          );
                          // Calculate remaining quantity needed
                          const currentTotal = rows.reduce(
                            (sum, row) =>
                              sum + (parseFloat(String(row.bag_item_dispatch_quantity)) || 0),
                            0
                          );
                          const remainingQty = quantity - currentTotal;
                          const bagAvailableQty = parseFloat(String(val?.available_quantity)) || 0;
                          
                          // If entered dispatch quantity (remaining) is less than bag available quantity,
                          // auto-fill with the remaining quantity, otherwise use bag available quantity
                          const autoFillQty = remainingQty > 0 && remainingQty < bagAvailableQty 
                            ? remainingQty 
                            : bagAvailableQty;
                          
                          formik.setFieldValue(
                            "bag_item_dispatch_quantity",
                            autoFillQty > 0 ? autoFillQty : ""
                          );
                        } else {
                          formik.setFieldValue("available_quantity", "");
                          formik.setFieldValue("bag_item_dispatch_quantity", "");
                        }
                        // Cancel edit mode when bag selection changes
                        if (editingBagId !== null) {
                          setEditingBagId(null);
                        }
                      }}
                      onBlur={() => formik.setFieldTouched("bag_no", true)}
                      getOptionLabel={(option: BagOption) =>
                        option
                          ? `${option.bag_no} (Available: ${option.available_quantity})`
                          : ""
                      }
                      renderOption={(props, option: BagOption) =>
                        autocompleteDropdownStyles.renderOption(
                          props,
                          option,
                          (opt) =>
                            `${opt?.bag_no || ""} (Available: ${
                              opt?.available_quantity || 0
                            })`
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Bag No"
                          error={
                            formik.touched.bag_no && Boolean(formik.errors.bag_no)
                          }
                          helperText={
                            formik.touched.bag_no &&
                            typeof formik.errors.bag_no === "string"
                              ? formik.errors.bag_no
                              : undefined
                          }
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiInputBase-root": {
                              height: "32px",
                            },
                            "& .MuiInputBase-input": {
                              padding: "6px 12px",
                              height: "20px",
                            },
                          }}
                        />
                      )}
                      ListboxProps={{
                        ...autocompleteDropdownStyles.ListboxProps,
                        style: {
                          maxHeight: "200px",
                          overflow: "auto",
                          zIndex: 1400,
                        },
                      }}
                      componentsProps={{
                        popper: {
                          style: {
                            zIndex: 1400,
                          },
                          modifiers: [
                            {
                              name: "preventOverflow",
                              enabled: true,
                            },
                          ],
                        },
                      }}
                      disablePortal={false}
                    />
                  </td>
                  <td style={tableStyles.cell}>
                    <TextField
                      size="small"
                      value={
                        editingBagId !== null
                          ? ""
                          : formik.values.available_quantity || ""
                      }
                      disabled
                      placeholder="Auto-filled from bag selection"
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  </td>
                  <td style={tableStyles.cell}>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Enter Bag Item Dispatch Quantity"
                      value={
                        editingBagId !== null
                          ? ""
                          : formik.values.bag_item_dispatch_quantity
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        formik.setFieldValue("bag_item_dispatch_quantity", value);
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        !!formik.errors.bag_item_dispatch_quantity &&
                        formik.touched.bag_item_dispatch_quantity
                      }
                      helperText={
                        formik.touched.bag_item_dispatch_quantity &&
                        typeof formik.errors.bag_item_dispatch_quantity ===
                          "string"
                          ? formik.errors.bag_item_dispatch_quantity
                          : undefined
                      }
                      disabled={editingBagId !== null}
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  </td>
                  <td className="sticky-column" style={tableStyles.actionCell}>
                    <div style={tableStyles.actionButtonContainer}>
                      <SubmitButton
                        isSubmitting={false}
                        onClick={() => formik.handleSubmit()}
                        disabled={editingBagId !== null}
                      />
                    </div>
                  </td>
                </tr>
              )}

              {/* Loading State */}
              {loading ? (
                <tr>
                  <td
                    colSpan={readOnly ? 4 : 5}
                    style={tableStyles.cell}
                    className="text-center py-5"
                  >
                    <LoadingSpinner label="Loading bags..." />
                  </td>
                </tr>
              ) :(rows.length === 0 && bagOptions.length === 0 )? (
                <tr>
                  <td
                    colSpan={readOnly ? 4 : 5}
                    style={tableStyles.cell}
                    className="text-center py-5 text-muted"
                  >
                    <p className="mb-0 text-red">No bags available for this item.</p>
                    <p className="mb-0" style={{ fontSize: "0.875rem" }}>
                      Please ensure the item has available bags in the
                      warehouse.
                    </p>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={readOnly ? 4 : 5}
                    style={tableStyles.cell}
                    className="text-center py-2 text-muted"
                  >
                    No bags added. Please add at least one bag.
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => {
                  const isEditing = editingBagId === row.id;
                  const displayData = isEditing ? formik.values : row;

                  return (
                    <tr
                      key={row.id}
                      style={isEditing ? tableStyles.rowEditing : {}}
                    >
                      <td style={tableStyles.cell}>{idx + 1}</td>

                      {/* Bag No */}
                      <td style={tableStyles.cell}>
                        {isEditing ? (
                          <Autocomplete
                            size="small"
                            options={bagOptions || []}
                            getOptionLabel={(opt: BagOption) =>
                              `${opt?.bag_no || ""} (Available: ${
                                opt?.available_quantity || 0
                              })`
                            }
                            value={
                              displayData.bag_no &&
                              typeof displayData.bag_no === "object" &&
                              "bag_no" in displayData.bag_no
                                ? bagOptions.find((b) => {
                                    const bagNo =
                                      displayData.bag_no as BagOption;
                                    return (
                                      b.bag_no === bagNo.bag_no &&
                                      String(b.inventory_item_id) ===
                                        String(bagNo.inventory_item_id)
                                    );
                                  }) || null
                                : null
                            }
                            onChange={(_, value) => {
                              if (value) {
                                formik.setFieldValue("bag_no", value);
                                formik.setFieldValue(
                                  "available_quantity",
                                  value.available_quantity || ""
                                );
                                // Calculate remaining quantity needed (excluding the row being edited)
                                const rowsExcludingEdited = rows.filter(
                                  (row) => row.id !== editingBagId
                                );
                                const currentTotal = rowsExcludingEdited.reduce(
                                  (sum, row) =>
                                    sum + (parseFloat(String(row.bag_item_dispatch_quantity)) || 0),
                                  0
                                );
                                const remainingQty = quantity - currentTotal;
                                const bagAvailableQty = parseFloat(String(value?.available_quantity)) || 0;
                                
                                // If entered dispatch quantity (remaining) is less than bag available quantity,
                                // auto-fill with the remaining quantity, otherwise use bag available quantity
                                const autoFillQty = remainingQty > 0 && remainingQty < bagAvailableQty 
                                  ? remainingQty 
                                  : bagAvailableQty;
                                
                                formik.setFieldValue(
                                  "bag_item_dispatch_quantity",
                                  autoFillQty > 0 ? autoFillQty : ""
                                );
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                sx={{
                                  ...autocompleteDropdownStyles.inputSx,
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "6px 12px",
                                    height: "20px",
                                  },
                                }}
                              />
                            )}
                            renderOption={(props, option) =>
                              autocompleteDropdownStyles.renderOption(
                                props,
                                option,
                                (opt) =>
                                  `${opt?.bag_no || ""} (Available: ${
                                    opt?.available_quantity || 0
                                  })`
                              )
                            }
                            ListboxProps={{
                              ...autocompleteDropdownStyles.ListboxProps,
                              style: {
                                maxHeight: "200px",
                                overflow: "auto",
                                zIndex: 1400,
                              },
                            }}
                            componentsProps={{
                              popper: {
                                style: {
                                  zIndex: 1400,
                                },
                                modifiers: [
                                  {
                                    name: "preventOverflow",
                                    enabled: true,
                                  },
                                ],
                              },
                            }}
                            disablePortal={false}
                          />
                        ) : (
                          <span>{row.bag_no || "-"}</span>
                        )}
                      </td>

                      {/* Available Quantity */}
                      <td style={tableStyles.cell}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            value={displayData.available_quantity || ""}
                            disabled
                            fullWidth
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                              "& .MuiInputBase-root": {
                                height: "32px",
                              },
                              "& .MuiInputBase-input": {
                                padding: "6px 12px",
                                height: "20px",
                              },
                            }}
                          />
                        ) : (
                          <span>{row.available_quantity || row.bag_item_dispatch_quantity  || "-"}</span>
                        )}
                      </td>

                      {/* Dispatch Quantity */}
                      <td style={tableStyles.cell}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={displayData.bag_item_dispatch_quantity || ""}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "bag_item_dispatch_quantity",
                                e.target.value
                              )
                            }
                            fullWidth
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                              "& .MuiInputBase-root": {
                                height: "32px",
                              },
                              "& .MuiInputBase-input": {
                                padding: "6px 12px",
                                height: "20px",
                              },
                            }}
                          />
                        ) : (
                          <span>{row.bag_item_dispatch_quantity || "-"}</span>
                        )}
                      </td>

                      {/* Actions - Only show if not in read-only mode */}
                      {!readOnly && (
                        <td
                          style={
                            isEditing
                              ? tableStyles.actionCellEditing
                              : tableStyles.actionCell
                          }
                          className="sticky-column"
                        >
                          {isEditing ? (
                            <SaveCancelButtons
                              onSave={() => formik.handleSubmit()}
                              onCancel={handleCancelEdit}
                              containerStyle={tableStyles.actionButtonContainer}
                            />
                          ) : (
                            <div style={tableStyles.actionButtonContainer}>
                              {!isEditMode && (
                                <EditButton
                                  onClick={() => handleEdit(row)}
                                  iconClassName="ph-pencil"
                                />
                              )}
                              <DeleteButton
                                onClick={() => handleDelete(row.id)}
                                iconClassName="ph-trash"
                              />
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-center gap-3 py-3">
          {readOnly ? (
            <CancelBackButton label="Close" onClick={handleModalClose} />
          ) : (
            <>
              <CancelBackButton label="Cancel" onClick={handleModalClose} />
              <SubmitButton
                isSubmitting={formik.isSubmitting}
                labels={{
                  create: "Save",
                  updating: "Saving...",
                }}
                onClick={handleSave}
                disabled={loading}
              />
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DispatchBagSelectionModal;
